import * as THREE from 'three';
import { BeamNetwork } from '../gameplay/BeamRouting.js';
import { prefabs } from '../gameplay/Prefabs.js';

const _p = new THREE.Vector3();

/**
 * An instantiated level: scene graph, colliders, live entities and triggers.
 * Everything it created it can also tear down, so switching levels never
 * leaks geometry or leaves stale colliders in the physics world.
 */
export class LevelInstance {
  constructor(def, ctx) {
    this.def = def;
    this.ctx = ctx;
    this.group = new THREE.Group();
    this.group.name = def.id;
    this.entities = [];
    this.colliders = [];
    this.triggers = [];
    this.byRole = { shard: [], checkpoint: [], goal: [] };
    this.elapsed = 0;
  }

  update(dt, gameCtx) {
    this.elapsed += dt;
    this.beams?.update(dt, gameCtx);
    for (const e of this.entities) e.update?.(dt, this.elapsed, gameCtx);
  }

  /**
   * Trigger volumes are tested against the player's feet, waist and head so a
   * thin kill field cannot be skipped by a fast moving capsule.
   */
  testTriggers(points, gameCtx, rivalPass = false) {
    gameCtx.sweepPoints = points;
    for (const t of this.triggers) {
      if (!t.enabled) continue;
      // The rival's pass sees triggers that opted into serving any body
      // (pads, lifts) plus lethal ones (hazards, acid): machinery launches
      // it and hazards kill it, but it can never steal pickups, trip
      // checkpoints, or finish the level through the player's triggers.
      if (rivalPass && !t.anyBody && !t.lethal) continue;
      // Each pass keeps its own enter/exit edge. A shared flag lets the
      // rival standing in a volume swallow the player's onEnter.
      const key = rivalPass ? 'insideRival' : 'inside';
      let hit = false;
      for (const p of points) {
        if (Math.abs(p.x - t.center.x) <= t.half.x
         && Math.abs(p.y - t.center.y) <= t.half.y
         && Math.abs(p.z - t.center.z) <= t.half.z) { hit = true; break; }
      }
      if (hit && !t[key]) { t[key] = true; t.onEnter?.(gameCtx); }
      else if (!hit && t[key]) { t[key] = false; t.onExit?.(gameCtx); }
      if (hit) t.onStay?.(gameCtx.dt, gameCtx);
    }
  }

  dispose() {
    for (const e of this.entities) e.dispose?.();
    for (const c of this.colliders) this.ctx.world.remove(c);
    this.group.traverse((o) => {
      if (o.isMesh || o.isPoints || o.isSprite || o.isLine) {
        o.geometry?.dispose?.();
        // Shared library materials must survive; only per-object clones die.
        if (o.material?.userData?.owned) o.material.dispose();
      }
    });
    this.group.removeFromParent();
    this.colliders.length = 0;
    this.entities.length = 0;
    this.triggers.length = 0;
  }
}

/**
 * Turns level data into a LevelInstance. Object types are looked up in the
 * prefab registry, so content is data and code stays closed for modification.
 */
export function buildLevel(def, ctx) {
  const level = new LevelInstance(def, ctx);

  for (const obj of def.objects ?? []) {
    if (!obj || obj.disabled) continue;
    if (!prefabs.has(obj.t)) {
      console.warn(`[level] unknown prefab "${obj.t}" in ${def.id}`);
      continue;
    }
    let built;
    try {
      built = prefabs.create(obj.t, obj, ctx);
    } catch (err) {
      console.error(`[level] prefab "${obj.t}" failed`, err, obj);
      continue;
    }
    if (!built) continue;

    for (const o of built.objects ?? []) {
      // Which def entry produced this node. Tooling (the editor's picking,
      // probes) needs the mapping; nothing in gameplay reads it.
      o.userData.src = obj;
      level.group.add(o);
    }
    for (const c of built.colliders ?? []) level.colliders.push(c);
    for (const t of built.triggers ?? []) level.triggers.push(t);
    if (built.update || built.dispose) level.entities.push(built);
    if (built.role && level.byRole[built.role]) level.byRole[built.role].push(built);
  }

  if (level.entities.some(e => ['emitter', 'receptacle', 'gate'].includes(e.role))) {
    level.beams = new BeamNetwork(level.entities, ctx.world);
  }

  ctx.scene.add(level.group);

  level.spawn = {
    position: new THREE.Vector3().fromArray(def.spawn?.p ?? [0, 2, 0]),
    yaw: THREE.MathUtils.degToRad(def.spawn?.yaw ?? 0),
  };
  level.killY = def.killY ?? (level.spawn.position.y - 400);
  level.env = def.env ?? {};
  level.shardTotal = level.byRole.shard.length;

  return level;
}

/** Convenience for game modes that need to move the goal's lock state. */
export function setGoalUnlocked(level, unlocked) {
  for (const g of level.byRole.goal) g.api?.setUnlocked(unlocked);
}

export { _p };
